import React, { Component } from 'react';
import { formatNumber } from './Helpers';
import Table from "./Table";
import { TransactionTypes, TransactionTypeKeys } from './Types';

enum TransactionAcquiredDisposedCodes
{
	A = 'A',
	B = 'B'
}
type TransactionAcquiredDisposedCodeKeys = keyof typeof TransactionAcquiredDisposedCodes;

export interface DirectorDeal
{
	name: string
	officerTitle: string
	filingDate: string
	transactionType: TransactionTypeKeys
	transactionDate: string
	transactionAmount: string
	transactionPrice: string
	transactionValue: string
	currency: string
	transactionADCode?: TransactionAcquiredDisposedCodeKeys
	directorInfoUrl?: string
	symbol?: string
	company?: string
	companyInfoUrl?: string
	currentPrice?: string
	changePercentage?: string
	changeValue?: string
}

export interface DirectorDealsProps
{
	deals: DirectorDeal[]
	largeTable?: boolean
	sorting?: boolean
	defaultTableClassName?: string
	alignRightClassName?: string
}

export default class DirectorDeals extends Component<DirectorDealsProps>
{
	static defaultProps = {
		largeTable: false,
		sorting: false,
		alignRightClassName: 'text-align-right text-align-left-sm'
	};

	render()
	{
		return (
			<Table
				defaultClassName={this.props.defaultTableClassName}
				theadRows={this.headRows()}
				tbodyRows={this.bodyRows()}
			/>
		);
	}

	protected headRows = (): JSX.Element[] => 
	{
		const sortableLeftAlign = this.props.sorting ? 'sortable':'';
		const sortableRightAlign = this.props.alignRightClassName + (this.props.sorting ? ' sortable':'');
		if ( this.props.largeTable ) {
			return [
				<tr key="head">
					<th>Declared</th>
					<th>Dealt</th>
					<th>Type</th>
					<th>EPIC</th>
					<th>Company</th>
					<th>Director</th>
					<th className={this.props.alignRightClassName}>Shares</th>
					<th className={this.props.alignRightClassName}>Price</th>
					<th className={this.props.alignRightClassName}>Value</th>
					<th className={this.props.alignRightClassName}>Cur. Price</th>
					<th className={this.props.alignRightClassName}>Change</th>
				</tr>
			];
		}
		else {
			return [
				<tr key="head">
					<th className={sortableLeftAlign} data-testid="dateTableHead">Date</th>
					<th data-testid="directorTableHead">Director</th>
					<th className={sortableLeftAlign} data-testid="dealTypeTableHead">Deal type</th>
					<th className={sortableRightAlign} data-testid="sharesDealtTableHead">Number of shares dealt</th>
					<th className={sortableRightAlign} data-testid="priceTableHead">Price</th>
					<th className={sortableRightAlign} data-testid="valueTradeTableHead">Value of trade</th>
				</tr>
			];
		}
	};

	protected bodyRows = (): JSX.Element[] =>
	{
		// iterate through deals and create table rows
		if ( this.props.largeTable ) {
			// large table
			return this.props.deals.map( (item, i) => {
				// create table row
				let symbolElem: JSX.Element, companyElem: JSX.Element;
				if ( item.companyInfoUrl !== undefined ) {
					symbolElem = <a href={item.companyInfoUrl}>{item.symbol}</a>;
					companyElem = <a href={item.companyInfoUrl}>{item.company}</a>;
				}
				else {
					symbolElem = <span>{item.symbol}</span>;
					companyElem = <span>{item.company}</span>;
				}
				// right aligned change classname
				let changeTextRightAlign: string;
				if ( item.changeValue !== undefined ) {
					changeTextRightAlign = `${this.props.alignRightClassName} ${this.changeClassname(parseFloat(item.changeValue.replace(/[^0-9\-\.]/g,'')))}`;
				}
				// change column value
				const showCurrentPricing = this.showCurrentPricingInfo(item);
				let changeValue: string;
				if ( showCurrentPricing ) {
					changeValue = `${item.changeValue} (${item.changePercentage}%)` ;
				}
				return (
					<tr key={i}>
						<td aria-label="Declaration date" scope="row">{item.filingDate}</td>
						<td aria-label="Dealing date" scope="row">{item.transactionDate}</td>
						<td aria-label="Deal type" className={this.transactionTypeClassname(item)}>
							{TransactionTypes[item.transactionType]}
						</td>
						<td aria-label="Instrument identifier">
							{symbolElem}
						</td>
						<td aria-label="Instrument name">
							{companyElem}
						</td>
						<td aria-label="Director name / Director's position" scope="row">
							{this.directorName(item)}
							{item.officerTitle}
						</td>
						<td aria-label="Number of shares dealt" className={this.props.alignRightClassName}>
							{item.transactionAmount}
						</td>
						<td aria-label="Price" className={this.props.alignRightClassName}>
							{item.transactionPrice}
						</td>
						<td aria-label="Value of trade" className={this.props.alignRightClassName}>
							{item.transactionValue}
						</td>
						<td aria-label="Current price" className={this.props.alignRightClassName}>
							{showCurrentPricing === true &&
							item.currentPrice
							}
						</td>
						<td aria-label="Value change" className={changeTextRightAlign}>
							{showCurrentPricing === true &&
							changeValue
							}
						</td>
					</tr>
				);
			});
		}
		else {
			// small table
			return this.props.deals.map( (item, i) => {
				// create table row
				return (
					<tr key={i}>
						<td aria-label="Date" scope="row" data-testid="dateValue">{item.filingDate}</td>
						<td aria-label="Director name / Director's position" scope="row" data-testid="directorValue">
							{this.directorName(item)}
							{item.officerTitle}
						</td>
						<td aria-label="Deal type" className={this.transactionTypeClassname(item)} data-testid="dealTypeValue">
							{TransactionTypes[item.transactionType]}
						</td>
						<td aria-label="Number of shares dealt" className={this.props.alignRightClassName} data-testid="sharesDealtValue">
							{item.transactionAmount}
						</td>
						<td aria-label="Price" className={this.props.alignRightClassName} data-testid="priceValue">
							£{item.transactionPrice}
						</td>
						<td aria-label="Value of trade" className={this.props.alignRightClassName} data-testid="valueTradeValue">
							£{formatNumber(Number(item.transactionValue), 0, true)}
						</td>
					</tr>
				);
			});
		}
	};

	protected directorName = (deal: DirectorDeal): JSX.Element =>
	{
		if ( deal.directorInfoUrl !== undefined ) {
			return <a className="font-weight-bold block" href={deal.directorInfoUrl}>{deal.name}</a>;
		}
		else {
			return <span className="font-weight-bold block">{deal.name}</span>;
		}
	};

	protected transactionTypeClassname = (deal: DirectorDeal): string =>
	{
		if ( deal.transactionADCode !== undefined ) {
			if ( deal.transactionADCode == TransactionAcquiredDisposedCodes.A ) {
				// aquisition / buy
				return this.changeClassname(1);
			}
			else {
				// disposition / sell
				return this.changeClassname(-1);
			}
		}
		return '';
	};

	protected changeClassname = (value: number): string =>
	{
		if ( value > 0 ) {
			return 'up-text';
		}
		else if ( value < 0 ) {
			return 'down-text';
		}
		return '';
	};

	/**
	 * Whether to show current pricing information on a large table
	 */
	protected showCurrentPricingInfo = (deal: DirectorDeal): boolean =>
	{
		// currentPrice, changeValue and changePercentage values must be present
		if ( deal.currentPrice === undefined || deal.changeValue === undefined || deal.changePercentage === undefined ) {
			return false;
		}
		const transactionValue = parseFloat(deal.transactionValue.replace(/[^0-9\-\.]/g,''));
		// must be an Aquisition with a transactionValue
		return deal.transactionADCode == TransactionAcquiredDisposedCodes.A && transactionValue > 0;
	};
}